import React from 'react';
import { useIntl } from 'react-intl';
import { useHotkeys } from 'react-hotkeys-hook';
import ContextButton from '../ContextButton';
import { useCore } from 'contexts/core';
import { useBehaviorTracker } from 'contexts/behaviors';
import { CoreApi, NonBehaviorDeleteComponent } from '@inter-ikea-storageone/range';
import TrashCanIcon from '@ingka/ssr-icon/paths/trash-can';
import { Entity } from '@inter-ikea-core/core';
import { useSelected } from 'contexts/selected';
import { Behaviors } from 'rangeConfig/@types';
import styled from 'styled-components';

const DisabledText = styled.div`
  display: none;
  :not(:focus):not(:active) {
    clip: rect(0 0 0 0);
    clip-path: inset(100%);
    height: 1px;
    overflow: hidden;
    position: absolute;
    white-space: nowrap;
    width: 1px;
  }
`;

type Props = {
  selectedEntity: Entity;
  size?: 'xsmall' | 'small' | 'medium';
};

const nonBehaviorDelete = (entity: Entity) => entity.hasComponent(NonBehaviorDeleteComponent.id);

export const deleteAction = (
  selectedEntity: Entity,
  selectedGroupId: string,
  coreApi: CoreApi,
  behaviorTracker: ReturnType<typeof useBehaviorTracker>,
  menuBehavior?: Behaviors
) => {
  if (nonBehaviorDelete(selectedEntity)) {
    coreApi.history.historyAction(() => {
      coreApi.entities.delete(selectedEntity);
    });
  } else {
    behaviorTracker
      .withContext({ menuType: 'scene', groupId: selectedGroupId })
      .trigger('productDeleted', null, menuBehavior);
  }
};

const Delete = ({ selectedEntity, size }: Props) => {
  const { formatMessage } = useIntl();
  const { selectedMenu, selectedGroup } = useSelected();

  const coreApi = useCore();
  const behaviorTracker = useBehaviorTracker();

  const selectedGroupId = selectedGroup?.id ?? '';

  const onDeleteClicked = () => {
    deleteAction(
      selectedEntity,
      selectedGroupId,
      coreApi,
      behaviorTracker,
      selectedMenu?.behaviors
    );
  };

  useHotkeys('backspace', onDeleteClicked, [selectedEntity, selectedGroupId]);
  useHotkeys('del', onDeleteClicked, [selectedEntity, selectedGroupId]);

  return (
    <ContextButton
      label={formatMessage({ id: 'storageone.contextMenuDelete' })}
      dataTestId="delete"
      onClick={onDeleteClicked}
      ssrIcon={TrashCanIcon}
      size={size}
    >
      <DisabledText>{formatMessage({ id: 'storageone.contextMenuDelete' })}</DisabledText>
    </ContextButton>
  );
};

export default Delete;
